/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import Loading from '../../components/Loading';
import SectionMeta, { SectionHeaderRow } from '../../components/SectionMeta';
import { listRoles } from '../../services/users/rolesApi';
import { notifyApiError } from '../../utils/feedback';

import { Container } from '../../styles/layout/Container';
import {
   PageHeader,
   PageTitle,
   PageSubtitle,
   PageHeaderText,
   PageHeaderActions,
} from '../../styles/layout/PageHeader';
import {
   Section,
   SectionHeader,
   SectionTitle,
   SectionBody,
} from '../../styles/layout/Section';
import { TableWrapper } from '../../styles/table/TableWrapper';
import { Table } from '../../styles/table/Table';
import { EmptyState } from '../../styles/feedback/EmptyState';

export default function Roles() {
   const navigate = useNavigate();

   const [roles, setRoles] = useState([]);
   const [isLoading, setIsLoading] = useState(false);

   const [sortConfig, setSortConfig] = useState({
      key: null,
      direction: null,
   });

   useEffect(() => {
      handleGetRoles();
   }, []);

   // FC1 desde 2026-09-15 (0111): o service lê `data.roles` do envelope JSend
   // (e o array cru pré-deploy); o catch recebe `ApiError` normalizado — o
   // 401 é do saga de auth, não desta tela.
   const handleGetRoles = async () => {
      setIsLoading(true);
      try {
         const list = await listRoles();
         setRoles(list);
         setSortConfig({ key: null, direction: null });
      } catch (err) {
         notifyApiError(err);
      } finally {
         setIsLoading(false);
      }
   };

   const handleRowDoubleClick = (id) => {
      navigate(`/roles/${id}`);
   };

   const handleSort = (sortKey) => {
      setSortConfig((prevConfig) => {
         if (prevConfig.key !== sortKey) return { key: sortKey, direction: 'asc' };
         if (prevConfig.direction === 'asc') return { key: sortKey, direction: 'desc' };
         if (prevConfig.direction === 'desc') return { key: null, direction: null };
         return { key: sortKey, direction: 'asc' };
      });
   };

   const getSortIcon = (columnKey) => {
      if (sortConfig.key !== columnKey) return ' ⇅';
      if (sortConfig.direction === 'asc') return ' ▲';
      if (sortConfig.direction === 'desc') return ' ▼';
      return ' ⇅';
   };

   const sortedRoles = useMemo(() => {
      if (!sortConfig.key || !sortConfig.direction) return roles;

      return [...roles].sort((a, b) => {
         const aValue = a[sortConfig.key];
         const bValue = b[sortConfig.key];

         if (aValue === null || aValue === undefined) return 1;
         if (bValue === null || bValue === undefined) return -1;

         if (typeof aValue === 'number' && typeof bValue === 'number') {
            return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
         }

         const aString = String(aValue).toLowerCase();
         const bString = String(bValue).toLowerCase();

         if (aString < bString) return sortConfig.direction === 'asc' ? -1 : 1;
         if (aString > bString) return sortConfig.direction === 'asc' ? 1 : -1;
         return 0;
      });
   }, [roles, sortConfig]);

   return (
      <Container>
         <Loading isLoading={isLoading} />

         <PageHeader>
            <PageHeaderText>
               <PageTitle>Níveis de Acesso (Roles)</PageTitle>
               <PageSubtitle>
                  Visualize e gerencie os níveis de acesso do sistema. Dê duplo
                  clique na linha para ver detalhes e configurar permissões.
               </PageSubtitle>
            </PageHeaderText>

            <PageHeaderActions>
               <button
                  type="button"
                  onClick={() => navigate('/roles/new')}
               >
                  Novo Perfil de Acesso
               </button>
            </PageHeaderActions>
         </PageHeader>

         <Section>
            <SectionHeader>
               <SectionHeaderRow>
                  <SectionTitle>Roles cadastradas</SectionTitle>
                  <SectionMeta
                     items={[
                        {
                           key: 'count',
                           label: 'Total de roles:',
                           value: roles.length,
                        },
                     ]}
                  />
               </SectionHeaderRow>
            </SectionHeader>

            <SectionBody>
               {roles.length === 0 ? (
                  <EmptyState>
                     <h4>Sem roles cadastradas</h4>
                     <p>Não há níveis de acesso configurados no sistema.</p>
                  </EmptyState>
               ) : (
                  <TableWrapper>
                     <Table>
                        <thead>
                           <tr>
                              <th
                                 onClick={() => handleSort('id')}
                                 style={{ cursor: 'pointer', userSelect: 'none' }}
                                 title="Clique para ordenar"
                              >
                                 ID{getSortIcon('id')}
                              </th>
                              <th
                                 onClick={() => handleSort('name')}
                                 style={{ cursor: 'pointer', userSelect: 'none' }}
                                 title="Clique para ordenar"
                              >
                                 Nome{getSortIcon('name')}
                              </th>
                              <th
                                 onClick={() => handleSort('description')}
                                 style={{ cursor: 'pointer', userSelect: 'none' }}
                                 title="Clique para ordenar"
                              >
                                 Descrição{getSortIcon('description')}
                              </th>
                              <th
                                 onClick={() => handleSort('user_count')}
                                 style={{
                                    textAlign: 'center',
                                    cursor: 'pointer',
                                    userSelect: 'none',
                                 }}
                                 title="Clique para ordenar"
                              >
                                 Usuários{getSortIcon('user_count')}
                              </th>
                           </tr>
                        </thead>
                        <tbody>
                           {sortedRoles.map((role) => (
                              <tr
                                 key={String(role.id)}
                                 onDoubleClick={() => handleRowDoubleClick(role.id)}
                                 style={{ cursor: 'pointer' }}
                                 title="Duplo clique para ver detalhes"
                              >
                                 <td>{role.id}</td>
                                 <td>{role.name}</td>
                                 <td>{role.description || '-'}</td>
                                 <td style={{ textAlign: 'center' }}>
                                    {role.user_count}
                                 </td>
                              </tr>
                           ))}
                        </tbody>
                     </Table>
                  </TableWrapper>
               )}
            </SectionBody>
         </Section>
      </Container>
   );
}
